import React from 'react';

import { DownloadNonPaginatedDataCSV } from 'components/DownloadCSV';
import dayjs from 'dayjs';
import { Options } from 'export-to-csv';
import usePackageUtilization from 'hooks/reports/utilization/package-utilization/usePackageUtilization';
import { PackageUtilizationReport } from 'types/generated/models/PackageUtilizationReport';
import { capitalize } from 'utils/formatters';

import { PackageUtilizationSearchFilters } from '../../../SearchFilters';

interface DownloadCSVProps {
  className?: string;
  searchFilters: PackageUtilizationSearchFilters;
}

const csvOptions: Options = {
  fieldSeparator: ',',
  quoteStrings: '"',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const dataToRow = (d: PackageUtilizationReport): { [key: string]: any } => {
  let expectation = 'Multiple';
  if (d.expectedFrequencyUnit !== null && d.expectedFrequencyValue !== null) {
    const value = d.expectedFrequencyValue;
    const formattedValue = value % 1 === 0 ? value.toString() : value.toFixed(2).replace(/\.?0+$/, '');
    const unit = capitalize(d.expectedFrequencyUnit);
    expectation = `${formattedValue}x per ${unit}`;
  }

  const calculatedExpectation =
    d.calculatedExpectation % 1 === 0
      ? `${d.calculatedExpectation} sessions`
      : `${d.calculatedExpectation.toFixed(2).replace(/\.?0+$/, '')} sessions`;

  return {
    'Athlete Name': d.athleteFullName || 'All',
    Package: d.packageName || 'All',
    'Class Attendance': d.classAttendance,
    'Appointment Attendance': d.appointmentAttendance,
    'Total Attendance': d.totalAttendance,
    'Days Attended': d.daysAttended,
    Expectation: expectation,
    'Calculated Expectation': calculatedExpectation,
    'Utilization Rate': d.utilizationRate !== null ? `${(d.utilizationRate * 100).toFixed(1)}%` : '',
  };
};

const DownloadCSV = ({ className, searchFilters }: DownloadCSVProps): JSX.Element => {
  return (
    <DownloadNonPaginatedDataCSV
      useList={usePackageUtilization}
      dataToRow={dataToRow}
      csvOptions={csvOptions}
      hookOptions={searchFilters}
      className={className}
      reportType='package-utilization'
      startDate={searchFilters.startDate ? dayjs(searchFilters.startDate).format('YYYY-MM-DD') : null}
      endDate={searchFilters.endDate ? dayjs(searchFilters.endDate).format('YYYY-MM-DD') : null}
    />
  );
};

export default DownloadCSV;
